'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { strings } from '@/content/strings';
import { parseISOToLocalDate } from '@/lib/dates';

/**
 * Two-month availability calendar with navigation.
 * Unavailable dates are struck-through.
 * Supports simple range selection with min-stay hint on hover.
 */

type BlockedRange = {
  start: string; // ISO date
  end: string;   // ISO date
  source: 'booking' | 'ical' | 'manual';
};

type MinNightOverrideRange = {
  start: string;
  end: string;
  minNights: number;
};

interface AvailabilityCalendarProps {
  blocked: BlockedRange[];
  minNights?: number;
  maxNights?: number;
  minLeadDays?: number;
  minNightOverrides?: MinNightOverrideRange[];
  showMinNightHint?: boolean;
  showSelectionHint?: boolean;
  allowPastDates?: boolean;
  onRangeSelected?: (payload: { checkIn: string; checkOut: string }) => void;
  showLegend?: boolean;
  monthsToShow?: number;
  layout?: 'inline' | 'stacked';
  selectionMode?: 'range' | 'single';
  allowSameDayRange?: boolean;
  enforceMinNights?: boolean;
}

type Month = { year: number; month: number };

export default function AvailabilityCalendar({
  blocked,
  minNights,
  maxNights,
  minLeadDays,
  minNightOverrides = [],
  showMinNightHint = false,
  showSelectionHint = true,
  allowPastDates = false,
  onRangeSelected,
  showLegend = true,
  monthsToShow = 2,
  layout = 'inline',
  selectionMode = 'range',
  allowSameDayRange = false,
  enforceMinNights = true,
}: AvailabilityCalendarProps) {
  const calendarStrings = strings.common.calendar;
  const [startMonth, setStartMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const ranges = useMemo(
    () =>
      blocked.map((r) => ({
        start: parseISOToLocalDate(r.start),
        end: parseISOToLocalDate(r.end),
        source: r.source,
      })),
    [blocked]
  );
  const minNightRanges = useMemo(
    () =>
      minNightOverrides.map((r) => ({
        startKey: dateKeyLocal(parseISOToLocalDate(r.start)),
        endKey: dateKeyLocal(parseISOToLocalDate(r.end)),
        minNights: r.minNights,
      })),
    [minNightOverrides]
  );

  const months: Month[] = useMemo(() => buildMonths(startMonth, monthsToShow), [startMonth, monthsToShow]);

  const goNext = () => {
    const next = new Date(startMonth);
    next.setMonth(next.getMonth() + 1);
    setStartMonth(next);
  };

  const goPrev = () => {
    const prev = new Date(startMonth);
    prev.setMonth(prev.getMonth() - 1);
    // Prevent navigating before the current month
    if (!allowPastDates) {
      const today = new Date();
      const floor = new Date(today.getFullYear(), today.getMonth(), 1);
      if (prev < floor) return;
    }
    setStartMonth(prev);
  };

  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const selectedMinNights =
    rangeStart ? resolveMinNights(rangeStart, minNightRanges, minNights) : undefined;
  const hoverMinNights =
    hoveredDate ? resolveMinNights(hoveredDate, minNightRanges, minNights) : undefined;
  const hintMinNights = rangeStart ? selectedMinNights : hoveredDate ? hoverMinNights : undefined;
  const displayedMinNights = hintMinNights ?? minNights ?? 2;

  const onSelectDate = (day: Date) => {
    if (!allowPastDates && (isPast(day) || isTooSoon(day, minLeadDays))) return;
    if (selectionMode === 'single') {
      setRangeStart(day);
      setRangeEnd(day);
      setHoveredDate(day);
      dispatchRangeSelected(day, day, onRangeSelected);
      return;
    }
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(day);
      setRangeEnd(null);
      setHoveredDate(day);
      return;
    }
    if (day.getTime() === rangeStart.getTime()) {
      if (allowSameDayRange) {
        setRangeEnd(day);
        setHoveredDate(day);
        dispatchRangeSelected(rangeStart, day, onRangeSelected);
      } else {
        setRangeStart(day);
        setRangeEnd(null);
        setHoveredDate(day);
      }
      return;
    }
    if (day < rangeStart) {
      setRangeStart(day);
      setRangeEnd(null);
      setHoveredDate(day);
      return;
    }
    if (enforceMinNights) {
      const minNightsForStart = resolveMinNights(rangeStart, minNightRanges, minNights);
      const nightsFromStart = Math.round(
        (day.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (minNightsForStart && nightsFromStart < minNightsForStart) {
        return;
      }
    }
    setRangeEnd(day);
    setHoveredDate(day);
    dispatchRangeSelected(rangeStart, day, onRangeSelected);
  };

  const inlineGrid =
    monthsToShow === 1 ? 'grid-cols-[auto,1fr,auto]' : 'grid-cols-[auto,1fr,1fr,auto]';

  return (
    <div className="space-y-3">
      {showLegend && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 text-destructive">
            <span className="inline-block w-3 h-3 rounded-sm bg-destructive/10 border border-destructive/30" />
            {calendarStrings.unavailableDates}
          </span>
        </div>
      )}
      {showMinNightHint && (
        <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground space-y-1">
          <div>{calendarStrings.minimumNights(displayedMinNights)}</div>
          {(minLeadDays ?? 0) > 0 && (
            <div>{calendarStrings.leadTime(minLeadDays ?? 0)}</div>
          )}
        </div>
      )}
      {layout === 'stacked' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              onClick={goPrev}
              className="rounded-full border border-border bg-card w-10 h-10 text-foreground hover:bg-primary/10 disabled:opacity-40"
              disabled={!allowPastDates && isStartAtCurrentMonth(startMonth)}
              aria-label={calendarStrings.prevMonth}
            >
              ‹
            </button>
            <button
              onClick={goNext}
              className="rounded-full border border-border bg-card w-10 h-10 text-foreground hover:bg-primary/10"
              aria-label={calendarStrings.nextMonth}
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {months.map((m) => (
              <div
                key={`${m.year}-${m.month}`}
                className="rounded-xl border border-border p-4 bg-card min-h-[320px] min-w-[260px]"
              >
                <div className="flex items-center justify-between text-sm font-semibold text-foreground mb-2">
                  <span>{formatMonthLabel(m)}</span>
                </div>
                <CalendarGrid
                  month={m}
                  blocked={ranges}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  onSelectDate={onSelectDate}
                  minNights={minNights}
                  minLeadDays={minLeadDays}
                  minNightOverrides={minNightRanges}
                  onHoverDate={setHoveredDate}
                  showSelectionHint={showSelectionHint}
                  allowPastDates={allowPastDates}
                  enforceMinNights={enforceMinNights}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`grid ${inlineGrid} gap-3 items-center`}>
          <button
            onClick={goPrev}
            className="rounded-full border border-border bg-card w-10 h-10 text-foreground hover:bg-primary/10 disabled:opacity-40"
            disabled={!allowPastDates && isStartAtCurrentMonth(startMonth)}
            aria-label={calendarStrings.prevMonth}
          >
            ‹
          </button>
          {months.map((m) => (
            <div
              key={`${m.year}-${m.month}`}
              className="rounded-xl border border-border p-4 bg-card min-h-[320px] min-w-[260px]"
            >
              <div className="flex items-center justify-between text-sm font-semibold text-foreground mb-2">
                <span>{formatMonthLabel(m)}</span>
              </div>
                <CalendarGrid
                  month={m}
                  blocked={ranges}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  onSelectDate={onSelectDate}
                  minNights={minNights}
                  minLeadDays={minLeadDays}
                  minNightOverrides={minNightRanges}
                  onHoverDate={setHoveredDate}
                  showSelectionHint={showSelectionHint}
                  allowPastDates={allowPastDates}
                  enforceMinNights={enforceMinNights}
                />
            </div>
          ))}
          <button
            onClick={goNext}
            className="rounded-full border border-border bg-card w-10 h-10 text-foreground hover:bg-primary/10"
            aria-label={calendarStrings.nextMonth}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

function buildMonths(base: Date, count: number): Month[] {
  return Array.from({ length: count }, (_, offset) => {
    const d = new Date(base);
    d.setMonth(d.getMonth() + offset);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
}

function isStartAtCurrentMonth(start: Date) {
  const today = new Date();
  return start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth();
}

function dispatchRangeSelected(
  start: Date,
  end: Date,
  onRangeSelected?: (payload: { checkIn: string; checkOut: string }) => void
) {
  if (typeof window === 'undefined') return;
  const payload = {
    checkIn: dateKeyLocal(start),
    checkOut: dateKeyLocal(end),
  };
  window.dispatchEvent(new CustomEvent('bookingRangeSelected', { detail: payload }));
  if (onRangeSelected) onRangeSelected(payload);
}

function formatMonthLabel({ year, month }: Month) {
  return new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function startDay(year: number, month: number) {
  return new Date(year, month, 1).getDay(); // 0=Sun
}

function isBlocked(date: Date, ranges: Array<{ start: Date; end: Date }>) {
  return ranges.some((r) => date >= r.start && date <= r.end);
}

function isPast(date: Date) {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return date < todayStart;
}

function isTooSoon(date: Date, minLeadDays?: number) {
  if (!minLeadDays || minLeadDays <= 0) return false;
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const earliest = new Date(todayStart);
  earliest.setDate(earliest.getDate() + minLeadDays);
  return date < earliest;
}

function resolveMinNights(
  date: Date,
  overrides: Array<{ startKey: string; endKey: string; minNights: number }> | undefined,
  fallback?: number
) {
  if (!overrides || overrides.length === 0) return fallback;
  const key = dateKeyLocal(date);
  const matches = overrides.filter((override) => key >= override.startKey && key <= override.endKey);
  if (matches.length === 0) return fallback;
  return Math.max(...matches.map((override) => override.minNights));
}

function dateKeyLocal(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateKey(key: string) {
  const [year, month, day] = key.split('-').map((part) => parseInt(part, 10));
  return new Date(year, month - 1, day);
}

function CalendarGrid({
  month,
  blocked,
  rangeStart,
  rangeEnd,
  onSelectDate,
  minNights,
  minLeadDays,
  minNightOverrides,
  onHoverDate,
  showSelectionHint = true,
  allowPastDates = false,
  enforceMinNights = true,
}: {
  month: Month;
  blocked: Array<{ start: Date; end: Date; source: string }>;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onSelectDate: (day: Date) => void;
  minNights?: number;
  minLeadDays?: number;
  minNightOverrides?: Array<{ startKey: string; endKey: string; minNights: number }>;
  onHoverDate?: (day: Date | null) => void;
  showSelectionHint?: boolean;
  allowPastDates?: boolean;
  enforceMinNights?: boolean;
}) {
  const calendarStrings = strings.common.calendar;
  const gridRef = useRef<HTMLDivElement>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<{ x: number; y: number } | null>(null);
  const [selectedAnchor, setSelectedAnchor] = useState<{ x: number; y: number } | null>(null);
  const totalDays = daysInMonth(month.year, month.month);
  const firstDay = startDay(month.year, month.month);
  const cells: Array<Date | null> = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) {
    cells.push(new Date(month.year, month.month, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: Array<Array<Date | null>> = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const leadStart = new Date(todayStart);
  if (minLeadDays && minLeadDays > 0) {
    leadStart.setDate(leadStart.getDate() + minLeadDays);
  }
  const selectedKey = rangeStart ? dateKeyLocal(rangeStart) : null;
  const activeKey = hoverKey ?? selectedKey;
  const activeMinNights = activeKey
    ? resolveMinNights(parseDateKey(activeKey), minNightOverrides, minNights) ?? minNights ?? 2
    : minNights ?? 2;
  const activeAnchor = hoverAnchor ?? selectedAnchor;
  const showHint =
    showSelectionHint && activeAnchor && (activeMinNights || (minLeadDays && minLeadDays > 0));

  const updateAnchorFromCell = (cell: HTMLElement | null) => {
    if (!gridRef.current || !cell) return;
    const containerRect = gridRef.current.getBoundingClientRect();
    const rect = cell.getBoundingClientRect();
    setHoverAnchor({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top,
    });
  };

  useEffect(() => {
    if (!selectedKey || !gridRef.current) {
      setSelectedAnchor(null);
      return;
    }
    const cell = gridRef.current.querySelector(`[data-date-key="${selectedKey}"]`) as HTMLElement | null;
    if (!cell) {
      setSelectedAnchor(null);
      return;
    }
    const containerRect = gridRef.current.getBoundingClientRect();
    const rect = cell.getBoundingClientRect();
    setSelectedAnchor({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top,
    });
  }, [selectedKey, month.year, month.month]);

  return (
    <div className="text-xs text-foreground">
      <div className="grid grid-cols-7 gap-1 mb-1 text-muted-foreground">
        {calendarStrings.daysShort.map((d) => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>
      <div
        className="space-y-1 relative"
        ref={gridRef}
        onPointerOver={(event) => {
          if (!onHoverDate) return;
          const target = event.target as HTMLElement;
          const cell = target.closest('[data-date-key]') as HTMLElement | null;
          const key = cell?.dataset.dateKey;
          if (!key) return;
          const next = parseDateKey(key);
          if (!allowPastDates && isPast(next)) return;
          setHoverKey(key);
          updateAnchorFromCell(cell);
          onHoverDate(next);
        }}
        onPointerMove={(event) => {
          if (!onHoverDate) return;
          const target = event.target as HTMLElement;
          const cell = target.closest('[data-date-key]') as HTMLElement | null;
          const key = cell?.dataset.dateKey;
          if (!key) return;
          const next = parseDateKey(key);
          if (!allowPastDates && isPast(next)) return;
          setHoverKey(key);
          updateAnchorFromCell(cell);
          onHoverDate(next);
        }}
        onPointerLeave={() => {
          if (onHoverDate) onHoverDate(null);
          setHoverKey(null);
          setHoverAnchor(null);
        }}
      >
        {showHint && (
          <div
            className="absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-card px-2 py-1 text-[11px] text-foreground shadow-sm"
            style={{ left: activeAnchor.x, top: activeAnchor.y - 6 }}
          >
            <div>{calendarStrings.minimumNights(activeMinNights)}</div>
            {(minLeadDays ?? 0) > 0 && (
              <div className="text-muted-foreground">
                {calendarStrings.leadTime(minLeadDays ?? 0)}
              </div>
            )}
          </div>
        )}
        {weeks.map((week, idx) => (
          <div key={idx} className="grid grid-cols-7 gap-1">
            {week.map((day, i) => {
              if (!day) return <div key={i} className="h-9 rounded-md" />;
              const blockedDay = isBlocked(day, blocked);
              const pastDay = !allowPastDates && day < todayStart;
              const tooSoon =
                !allowPastDates && !pastDay && minLeadDays && minLeadDays > 0 ? day < leadStart : false;
              const isToday =
                day.getFullYear() === today.getFullYear() &&
                day.getMonth() === today.getMonth() &&
                day.getDate() === today.getDate();
              const inRange =
                rangeStart &&
                rangeEnd &&
                day >= rangeStart &&
                day <= rangeEnd &&
                !(rangeStart.getFullYear() === day.getFullYear() &&
                  rangeStart.getMonth() === day.getMonth() &&
                  rangeStart.getDate() === day.getDate());
              const isStart =
                rangeStart &&
                day.getFullYear() === rangeStart.getFullYear() &&
                day.getMonth() === rangeStart.getMonth() &&
                day.getDate() === rangeStart.getDate();
              const isEnd =
                rangeEnd &&
                day.getFullYear() === rangeEnd.getFullYear() &&
                day.getMonth() === rangeEnd.getMonth() &&
                day.getDate() === rangeEnd.getDate();
              const nightsFromStart =
                rangeStart && !rangeEnd
                  ? Math.round((day.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24))
                  : null;
              const minNightsForDay = resolveMinNights(day, minNightOverrides, minNights);
              const minNightsForStart = rangeStart
                ? resolveMinNights(rangeStart, minNightOverrides, minNights)
                : minNights;
              const belowMinStay =
                enforceMinNights &&
                rangeStart &&
                !rangeEnd &&
                nightsFromStart !== null &&
                nightsFromStart > 0 &&
                minNightsForStart &&
                nightsFromStart < minNightsForStart;
              const pastClass = pastDay && !blockedDay
                ? 'line-through text-muted-foreground bg-muted/30 cursor-not-allowed'
                : '';
              const leadClass = !pastDay && !blockedDay && tooSoon
                ? 'text-muted-foreground bg-muted/30 cursor-not-allowed'
                : '';
              const disabledClass = !pastDay && !tooSoon && belowMinStay
                ? 'text-muted-foreground bg-muted/30 cursor-not-allowed'
                : '';
              return (
                <div
                  key={i}
                  className={`h-9 flex items-center justify-center rounded-md border text-[11px] bg-background border-border transition-colors ${
                    isToday ? 'ring-1 ring-primary' : ''
                  } ${blockedDay ? 'line-through text-destructive bg-destructive/10 cursor-not-allowed border-destructive/30' : ''} ${
                    pastClass || disabledClass || leadClass || blockedDay ? '' : 'cursor-pointer'
                  } ${pastClass} ${leadClass} ${disabledClass} ${inRange ? 'bg-primary/10 border-primary/30' : ''} ${
                    isStart || isEnd ? 'bg-primary/20 border-primary font-semibold' : ''
                  }`}
                  onClick={(event) => {
                    if (blockedDay || pastDay || tooSoon || belowMinStay) return;
                    const cell = event.currentTarget as HTMLElement;
                    const key = cell.dataset.dateKey;
                    if (key) {
                      setHoverKey(key);
                      updateAnchorFromCell(cell);
                    }
                    onSelectDate(day);
                  }}
                  data-date-key={dateKeyLocal(day)}
                >
                  {day.getDate()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
